import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import TripChat from '../chat/TripChat';
import axios from 'axios';
import { 
  MapPin, 
  User, 
  Calendar, 
  Clock,
  Users, 
  Car,
  AlertCircle,
  MessageSquare,
  Star,
  Navigation,
  CheckCircle,
  X,
  Phone,
  Mail
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

interface TripStop {
  id?: number;
  name: string;
  latitude: number;
  longitude: number;
  address?: string;
  is_pickup_point: boolean;
  is_dropoff_point: boolean;
}

interface Trip {
  id: number;
  route_id?: number | null;
  route_name?: string;
  start_location?: string;
  destination?: string;
  stop_points?: TripStop[];
  driver_id: number;
  driver_first_name?: string;
  driver_last_name?: string;
  driver_email?: string;
  driver_phone?: string;
  driver_profile_picture?: string;
  departure_time: string;
  available_seats: number;
  remaining_seats?: number;
  price_per_seat?: number;
  currency: string;
  vehicle_info?: {
    make?: string;
    model?: string;
    color?: string;
    license_plate?: string;
  };
  special_instructions?: string;
  trip_status: 'scheduled' | 'active' | 'cancelled' | 'completed';
  pickup_locations?: string[];
  dropoff_locations?: string[];
  created_at: string;
  updated_at: string;
}

interface Route {
  id: number;
  name: string;
  description?: string;
  start_location: string;
  end_location: string;
  start_latitude?: number;
  start_longitude?: number;
  end_latitude?: number;
  end_longitude?: number;
  distance_km?: number;
  estimated_duration_minutes?: number;
  stop_points?: Array<{
    name: string;
    latitude: number;
    longitude: number;
    address?: string;
    is_pickup_point: boolean;
    is_dropoff_point: boolean;
  }>;
}

interface Driver {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  rating?: number;
  total_trips?: number;
  profile_picture?: string;
}

interface TripRequest {
  id: number;
  trip_id: number;
  passenger_id: number;
  pickup_location?: string;
  dropoff_location?: string;
  requested_seats: number;
  message?: string;
  request_status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

const TripP: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [trip, setTrip] = useState<Trip | null>(null);
  const [route, setRoute] = useState<Route | null>(null);
  const [driver, setDriver] = useState<Driver | null>(null);
  const [userTripRequest, setUserTripRequest] = useState<TripRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [showChat, setShowChat] = useState(false);
  
  // Request form state
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestData, setRequestData] = useState({
    pickup_location: '',
    dropoff_location: '',
    seats_requested: 1,
    message: ''
  });

  useEffect(() => {
    if (tripId) {
      fetchTripDetails();
    }
  }, []);

  const fetchTripDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch trip details
      const tripResponse = await api.get(`/trips/${tripId}`);
      const tripData = tripResponse.data.trip;
      setTrip(tripData);
      setDriver({
        id: tripData.driver_id,
        first_name: tripData.driver_first_name || '',
        last_name: tripData.driver_last_name || '',
        email: tripData.driver_email || '',
        phone: tripData.driver_phone || '',
        rating: tripData.driver_rating || 0
      });
      // Route is optional for a trip
      if (tripData.route_id) {
        try {
          const routeResponse = await api.get(`/routes/${tripData.route_id}`);
          setRoute(routeResponse.data.route);
        } catch (routeErr) {
          console.error('Failed to fetch route details:', routeErr);
        }
      }

      // Fetch driver details
      //const driverResponse = await api.get(`/users/${tripData.driver_id}`);
      // setDriver(driverResponse.data.user);
      console.log("Here", tripData.driver_id);
      console.log("User id", user?.id);
      // Fetch user's trip request if they're not the driver
      if (user && tripData.driver_id !== user.id) {
        try {
          console.log("Fetching user trip request");
          const userRequestResponse = await api.get(`/trips/${tripId}/request-by-user`);
          setUserTripRequest(userRequestResponse.data.request);
        } catch (requestErr: any) {
          // If no request found (404), that's okay - user hasn't made a request yet
          if (requestErr.response?.status !== 404) {
            console.error('Failed to fetch user trip request:', requestErr);
          }
        }
      }

    } catch (err: any) {
      console.error('Failed to fetch trip details:', err);
      setError(err.response?.data?.error || 'Failed to load trip details');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinRequest = async () => {
    if (!trip || !user) return;

    try {
      setRequestLoading(true);
      
      const requestPayload = {
        trip_id: trip.id,
        pickup_location: requestData.pickup_location || trip.start_location,
        dropoff_location: requestData.dropoff_location || trip.destination,
        seats_requested: requestData.seats_requested,
        message: requestData.message
      };

      await api.post(`/trips/${trip.id}/request`, requestPayload);

      setRequestSuccess(true);
      setShowRequestForm(false);
      
      // Refresh trip data to update available seats and fetch user trip request
      fetchTripDetails();
      
    } catch (err: any) {
      console.error('Failed to send join request:', err);
      setError(err.response?.data?.error || 'Failed to send join request');
    } finally {
      setRequestLoading(false);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getVehicleInfo = () => {
    if (!trip?.vehicle_info) return 'Vehicle info not provided';
    const { make, model, color, license_plate } = trip.vehicle_info;
    const parts = [make, model, color].filter(Boolean);
    const vehicle = parts.join(' ');
    return license_plate ? `${vehicle} (${license_plate})` : vehicle;
  };

  const canRequestJoin = () => {
    return user && trip && trip.driver_id !== user.id && 
           (trip.trip_status === 'scheduled' || trip.trip_status === 'active') && 
           trip.available_seats > 0 && !userTripRequest;
  };

  const isDriver = () => {
    return user && trip && trip.driver_id === user.id;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading trip details...</p>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error encountered</h2>
          <p className="text-gray-600 mb-4">{error || 'The requested trip could not be found.'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const departureDateTime = formatDateTime(trip.departure_time);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <Navigation className="h-4 w-4 mr-1 rotate-180" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Trip Details</h1>
        </div>

        {/* Success Message */}
        {requestSuccess && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Your join request has been sent successfully! The driver will review your request.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Overview */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">{route?.name || trip.route_name || `${trip.start_location} → ${trip.destination}`}</h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      trip.trip_status === 'active' ? 'bg-green-100 text-green-600' :
                      trip.trip_status === 'completed' ? 'bg-blue-100 text-blue-600' :
                      'bg-red-100 text-red-600'
                    }`}>
                      {trip.trip_status.charAt(0).toUpperCase() + trip.trip_status.slice(1)}
                    </span>
                    <span>Trip #{trip.id}</span>
                    <span>Driver: {[trip.driver_first_name, trip.driver_last_name].filter(Boolean).join(' ') || 'Loading...'}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-blue-600">
                    {trip.price_per_seat ? `${trip.currency} ${trip.price_per_seat}` : 'Free'}
                  </div>
                  <div className="text-sm text-gray-500">per seat</div>
                </div>
              </div>

              {/* Route Information */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Trip Information</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-green-500 mt-1" />
                    <div>
                      <div className="font-medium text-gray-900">Start Location</div>
                      <div className="text-gray-600">{trip.start_location}</div>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-red-500 mt-1" />
                    <div>
                      <div className="font-medium text-gray-900">Destination</div>
                      <div className="text-gray-600">{trip.destination}</div>
                    </div>
                  </div>
                  {route?.description && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <div className="font-medium text-gray-900 mb-1">Route Description</div>
                      <div className="text-gray-600">{route.description}</div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6 pt-4 border-t">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">
                      {route?.distance_km ? `${route.distance_km} km` : 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">Distance</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">
                      {formatDuration(route?.estimated_duration_minutes)}
                    </div>
                    <div className="text-sm text-gray-500">Duration</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900">
                      {trip.stop_points?.length || 0}
                    </div>
                    <div className="text-sm text-gray-500">Stop Points</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stop Points */}
            {trip.stop_points && trip.stop_points.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Stop Points</h3>
                <div className="space-y-3">
                  {trip.stop_points.map((stop, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{stop.name}</div>
                        {stop.address && (
                          <div className="text-sm text-gray-600">{stop.address}</div>
                        )}
                        <div className="flex space-x-2 mt-1">
                          {stop.is_pickup_point && (
                            <span className="bg-green-100 text-green-600 px-2 py-1 rounded-full text-xs">
                              Pickup
                            </span>
                          )}
                          {stop.is_dropoff_point && (
                            <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs">
                              Drop-off
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trip Schedule & Details */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Trip Schedule & Details</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  <div>
                    <div className="font-medium text-gray-900">Departure Date</div>
                    <div className="text-gray-600">{departureDateTime.date}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-blue-500" />
                  <div>
                    <div className="font-medium text-gray-900">Departure Time</div>
                    <div className="text-gray-600">{departureDateTime.time}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="h-5 w-5 text-blue-500" />
                  <div>
                    <div className="font-medium text-gray-900">Available Seats</div>
                    <div className="text-gray-600">
                      {trip.remaining_seats} seat{trip.remaining_seats !== 1 ? 's' : ''} available
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Car className="h-5 w-5 text-blue-500" />
                  <div>
                    <div className="font-medium text-gray-900">Vehicle</div>
                    <div className="text-gray-600">{getVehicleInfo()}</div>
                  </div>
                </div>
                {trip.special_instructions && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <div className="font-medium text-yellow-800">Special Instructions</div>
                        <div className="text-yellow-700 mt-1">{trip.special_instructions}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Driver Information */}
            {driver && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Driver Information</h3>
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{[driver.first_name, driver.last_name].filter(Boolean).join(' ')}</div>
                    {driver.rating && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Star className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="text-sm text-gray-600">
                          {driver.rating.toFixed(1)} ({driver.total_trips || 0} trips)
                        </span>
                      </div>
                    )}
                    {driver.phone && (
                      <div className="flex items-center space-x-2 mt-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{driver.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 mt-1">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{driver.email}</span>
                    </div>
                  </div>
                </div>
                
                {isDriver() && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="bg-blue-50 p-3 rounded-lg">
                      <div className="text-sm text-blue-800">
                        <strong>You are the driver</strong> for this trip
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Join Trip Action */}
            {canRequestJoin() && !requestSuccess && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Join This Trip</h3>
                
                {!showRequestForm ? (
                  <div className="space-y-4">
                    <div className="text-sm text-gray-600">
                      Interested in joining this trip? Send a request to the driver.
                    </div>
                    <button
                      onClick={() => setShowRequestForm(true)}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Request to Join
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Location
                      </label>
                      <input
                        type="text"
                        value={requestData.pickup_location}
                        onChange={(e) => setRequestData(prev => ({ ...prev, pickup_location: e.target.value }))}
                        placeholder={trip.start_location}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Drop-off Location
                      </label>
                      <input
                        type="text"
                        value={requestData.dropoff_location}
                        onChange={(e) => setRequestData(prev => ({ ...prev, dropoff_location: e.target.value }))}
                        placeholder={trip.destination}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Seats
                      </label>
                      <select
                        value={requestData.seats_requested}
                        onChange={(e) => setRequestData(prev => ({ ...prev, seats_requested: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {[...Array(Math.min(trip.available_seats, 4))].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1} seat{i > 0 ? 's' : ''}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message to Driver (Optional)
                      </label>
                      <textarea
                        value={requestData.message}
                        onChange={(e) => setRequestData(prev => ({ ...prev, message: e.target.value }))}
                        rows={3}
                        placeholder="Introduce yourself or share any relevant information..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowRequestForm(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleJoinRequest}
                        disabled={requestLoading}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {requestLoading ? 'Sending...' : 'Send Request'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Existing Trip Request Status */}
            {userTripRequest && !isDriver() && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Your Trip Request</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Status:</span>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                      userTripRequest.request_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      userTripRequest.request_status === 'approved' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {userTripRequest.request_status.charAt(0).toUpperCase() + userTripRequest.request_status.slice(1)}
                    </span>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    <div className="mb-2">
                      <strong>Seats Requested:</strong> {userTripRequest.requested_seats}
                    </div>
                    {userTripRequest.pickup_location && (
                      <div className="mb-2">
                        <strong>Pickup:</strong> {userTripRequest.pickup_location}
                      </div>
                    )}
                    {userTripRequest.dropoff_location && (
                      <div className="mb-2">
                        <strong>Drop-off:</strong> {userTripRequest.dropoff_location}
                      </div>
                    )}
                    {userTripRequest.message && (
                      <div className="mb-2">
                        <strong>Message:</strong> {userTripRequest.message}
                      </div>
                    )}
                    <div>
                      <strong>Requested:</strong> {new Date(userTripRequest.created_at).toLocaleDateString()}
                    </div>
                  </div>

                  {userTripRequest.request_status === 'pending' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-yellow-600 mr-2" />
                        <span className="text-sm text-yellow-800">
                          Your request is pending driver approval.
                        </span>
                      </div>
                    </div>
                  )}

                  {userTripRequest.request_status === 'approved' && (
                    <div className="space-y-3">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                          <span className="text-sm text-green-800">
                            Congratulations! Your request has been approved.
                          </span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => setShowChat(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <MessageSquare className="h-4 w-4" />
                        Chat with Driver
                      </button>
                    </div>
                  )}

                  {userTripRequest.request_status === 'rejected' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <X className="h-5 w-5 text-red-600 mr-2" />
                        <span className="text-sm text-red-800">
                          Your request was not approved. You may try contacting the driver or look for other trips.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Trip Actions for Driver */}
            {isDriver() && (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Trip Management</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate(`/trip/${trip.id}/requests`)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <MessageSquare className="h-4 w-4 inline mr-2" />
                    Manage Requests
                  </button>
                  <button
                    onClick={() => navigate(`/dashboard/trips/${trip.id}/edit`)}
                    className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Edit Trip
                  </button>
                </div>
              </div>
            )}

            {/* Trip Status Info */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Trip Information</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="text-gray-900">
                    {new Date(trip.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="text-gray-900">
                    {new Date(trip.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ${
                    trip.trip_status === 'active' ? 'text-green-600' :
                    trip.trip_status === 'completed' ? 'text-blue-600' :
                    'text-red-600'
                  }`}>
                    {trip.trip_status.charAt(0).toUpperCase() + trip.trip_status.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trip Chat Modal */}
      {showChat && userTripRequest && driver && (
        <TripChat
          tripId={trip?.id || 0}
          requestId={userTripRequest.id}
          otherParticipant={{
            id: driver.id,
            name: [driver.first_name, driver.last_name].filter(Boolean).join(' ')
          }}
          onClose={() => setShowChat(false)}
        />
      )}
    </div>
  );
};

export default TripP;